<template>
  <div class="meter-device-edit">

    <div>
      <v-row>
        <v-col cols="12">
          <v-form ref="form" @submit.prevent="save">

            <headline>
              {{ $tc('meter-device.meter-device') }}
            </headline>
            <v-row>
              <v-col cols="12">
                <form-field
                    type="select"
                    :label="$t('meter-device.type')"
                    v-model="form.type"
                    :items="types"
                    :rules="[
                      commonRules.required
                    ]"
                    col-field="9"
                    disabled
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <form-field
                    type="select"
                    :label="$t('meter-device.statement-mode')"
                    v-model="form.statementMode"
                    :items="statementModes"
                    :rules="[
                      commonRules.required
                    ]"
                    disabled
                />
              </v-col>
              <v-col cols="12" md="6">
                <form-field
                    type="select"
                    :label="$t('meter-device.statement-type')"
                    v-model="form.statementType"
                    :items="statementTypes"
                    :rules="[
                      commonRules.required
                    ]"
                    disabled
                />
              </v-col>
            </v-row>


            <headline class="mt-10">
              {{ $tc("general-information") }}
            </headline>
            <v-row>
              <v-col cols="12" md="6">
                <form-field
                    :label="$t('name')"
                    v-model="form.name"
                    :rules="[
                        commonRules.required,
                    ]"
                />
              </v-col>
              <v-col cols="12" md="6" v-if="!isConnectedVehicle">
                <form-field
                    :label="$t('external-reference')"
                    v-model="form.externalId"
                    :rules="[

                    ]"
                    :readonly="form.statementMode === 'API'"
                />
              </v-col>

            </v-row>
            <!-- Rendered directly (it already provides the form-field label/content grid); wrapping it
                 in an extra v-row/v-col broke the alignment. -->
            <cost-center-allocation-compact
                v-model="form.costCenters"
                :organization-id="form.organization?.id"
                :label="$tc('cost-center.allocations')"
            />
            <!-- Brand/model come from the connected vehicle itself → hidden for that type. -->
            <v-row  v-if="!isExternalOperator && !isConnectedVehicle">
              <v-col cols="12" md="6">
                <form-field
                    :label="$t('meter-device.brand')"
                    v-model="form.brand"
                    :rules="[

                    ]"
                />
              </v-col>

              <v-col cols="12" md="6">
                <form-field
                    :label="$t('meter-device.model')"
                    v-model="form.model"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" v-if="form.meterIdFromApi">
                <form-field
                    type="select"
                    :label="$t('meter-device.meter')"
                    v-model="form.meterIdFromApi"
                    :items="meters"
                    readonly
                />
              </v-col>
            </v-row>


            <headline class="mt-10">
              {{ $tc("customer.customer") }}
            </headline>
            <v-row>
              <v-col cols="12">
                <form-field
                    type="customer-select"
                    :label="$tc('customer.customer',1)"
                    v-model="form.customer.id"
                    selection-with-no-extra
                    clearable
                    col-field="9"
                />
              </v-col>
            </v-row>

            <!-- Connected vehicle: one of the customer's vehicles (filtered by customer). -->
            <template v-if="isConnectedVehicle">
              <v-row>
                <v-col cols="12" md="6">
                  <!-- Filter by CUSTOMER only — the vehicle may be personal and not belong to any org. -->
                  <form-field
                      type="vehicle-select"
                      :label="$tc('vehicle.vehicle')"
                      v-model="form.vehicleId"
                      :customer-id="form.customer.id"
                      :disabled="!form.customer.id"
                      clearable
                      @selected="onVehicleSelected"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <form-field
                      input-type="number"
                      v-model.number="form.geofenceRadiusM"
                      :label="$t('meter-device.connected-vehicle.geofence-radius')"
                      suffix="m"
                  />
                </v-col>
              </v-row>
              <v-row v-if="form.vehicleId && vehicleLicenceOk === false">
                <v-col cols="12">
                  <v-alert type="warning" text dense class="mb-0">
                    {{ $t('meter-device.connected-vehicle.licence-missing') }}
                  </v-alert>
                </v-col>
              </v-row>
            </template>

            <headline class="mt-10">
              {{ $tc("location.location") }}
            </headline>
            <v-row>
              <v-col cols="12" md="6">
                <form-field
                    :label="$t('name')"
                    :value="form.location?.name"
                    disabled
                />
              </v-col>
              <v-col cols="12" md="6" class="text-center">
                <form-field
                    :label="$t('organization.organization')"
                    :value="form.organization?.name"
                    disabled
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <form-field
                    :label="$t('street')"
                    :value="form.location?.address?.street"
                    disabled
                />
              </v-col>
              <v-col cols="12" md="6">
                <form-field
                    :label="$t('state')"
                    :value="form.location?.address?.state"
                    disabled
                />
              </v-col>
              <v-col cols="12" md="6">
                <form-field
                    :label="$t('city')"
                    :value="form.location?.address?.city"
                    disabled
                />
              </v-col>
              <v-col cols="12" md="6">
                <form-field
                    :label="$t('zipcode')"
                    :value="form.location?.address?.zipcode"
                    disabled
                />
              </v-col>
              <v-col cols="12" class="text-right">
                <v-btn
                    @click="$refs.locationModal.open()"
                    small
                >
                  {{ $t('charging-point.edit-location') }}
                </v-btn>

              </v-col>
            </v-row>

            <template v-if="!isExternalOperator">

              <headline class="mt-10">
                {{ $t("settings") }}
              </headline>
              <!-- No meter index for a connected vehicle (session-based): hide the index values,
                   keep only the €/kWh price below. -->
              <v-row v-if="!isConnectedVehicle">
                <v-col cols="12" md="6">
                  <form-field
                      input-type="number"
                      min="0"
                      :label="$t('meter-device.initial-meter-value')"
                      v-model.number="form.initialMeterValue"
                      :step="1"
                      @input="filterInput"
                      :rules="[

                      ]"
                  >
                    <template v-slot:append>
                      /{{ $t('usages.kwh') }}
                    </template>
                  </form-field>
                </v-col>
                <v-col cols="12" md="6">
                  <form-field
                      input-type="number"
                      min="0"
                      :label="$t('meter-device.max-meter')"
                      v-model.number="form.maxMeterValue"
                      :rules="[

                      ]"
                  >
                    <template v-slot:append>
                      /{{ $t('usages.kwh') }}
                    </template>
                  </form-field>
                </v-col>
              </v-row>
              <v-row v-if="!isConnectedVehicle">
                <v-col cols="12" md="6">
                  <form-field
                      :label="$t('meter-device.last-meter-value')"
                      v-model.number="form.lastMeterValue"
                      :rules="[]"
                     readonly
                  >
                    <template v-slot:append>
                      /{{ $t('usages.kwh') }}
                    </template>
                  </form-field>
                </v-col>
                <v-col cols="12" md="6">
                  <form-field
                      :label="$t('meter-device.last-meter-date')"
                      v-model="form.lastMeterDate"
                      type="date"
                  ></form-field>
                </v-col>

              </v-row>
              <v-row >

                <v-col cols="12" md="6">
                  <v-row class="form-field">
                    <v-col sm="6" class="label">
                  <span>
                    {{ $t('charging-point.energy-provider-kwh-price') }}
                  </span>
                    </v-col>
                    <v-col class="input-wrapper">
                      <meter-device-energy-price-module
                          :meter-device-id="meterDevice.id"
                      />
                    </v-col>
                  </v-row>


                </v-col>
              </v-row>
            </template>

            <template v-if="isExternalOperator">
              <headline class="mt-10">
                {{ $t("enums.meter-device.external-operator") }}
              </headline>
              <v-row>
                <v-col cols="12" md="6">
                  <form-field
                      type="select"
                      v-model="form.externalOperatorBillingCycle"
                      :items="billingCycles"
                      :label="$tc('meter-device.external-operator.billing-cycle')"
                      :rules="[
                      commonRules.required,
                  ]"
                  >
                  </form-field>
                </v-col>
                <v-col cols="12" md="6">
                  <form-field
                      type="select"
                      v-model="form.externalOperatorSupplier"
                      :items="externalOperatorSuppliers"
                      :label="$tc('meter-device.external-operator.supplier')"
                      item-value="id"
                      item-text="name"
                      :rules="[
                            commonRules.required,
                        ]"
                  >
                  </form-field>
                </v-col>
              </v-row>
            </template>

            <v-row class="mt-10">
              <v-col cols="12" class="text-center">
                <v-btn type="submit" color="primary">
                  {{ $t('save') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
    </div>

    <location-modal ref="locationModal" :location.sync="location" />

  </div>
</template>

<script>

import RulesMixin from "@mixins/RulesMixin"
import MeterDeviceRepository from "@repository/MeterDeviceRepository"
import LocationModal from "@pages/ChargingPoint/Detail/modals/LocationModal"
import MeterDeviceEnergyPriceModule from "@pages/MeterDevice/Detail/modals/MeterDeviceEnergyPriceModule.vue"
import CostCenterAllocationCompact from "@blocks/CostCenter/CostCenterAllocationCompact.vue"
import CostCenterRepository from "@repository/CostCenterRepository"
import VehicleRepository from "@repository/VehicleRepository"

export default {
  components: {
    MeterDeviceEnergyPriceModule,
    CostCenterAllocationCompact,
    LocationModal,
  },
  data() {
    return {
      form: {
        location: null,
        organization: null,
        customer: {},
        meterIdFromApi: null,
        meterNameFromApi: null,
        costCenters: [],
      },
      types:[],
      meters: [],
      externalOperatorSuppliers: [],
      OrganizationName: null,
      vehicleLicenceOk: null, // null = unknown, true/false = Connected Vehicle Meter licence active?
    }
  },

  mixins: [
    RulesMixin,
  ],


  mounted() {
    if (!this.currentUser.permissions.includes('OPERATOR_WRITE')) {
      this.$router.replace({name: 'dashboard'})
      return
    }
    this.loadTypes()
    this.loadExternalOperatorSuppliers()

    this.form = this.cloneObj(this.meterDevice)

    this.form.initialMeterValue = this.form.initialMeterValue / 1000
    this.form.lastMeterValue = this.form.lastMeterValue / 1000
    this.form.maxMeterValue = this.form.maxMeterValue / 1000

    // Read model embeds the vehicle object → derive the id the vehicle-select binds to.
    if (this.form.vehicle) {
      this.$set(this.form, 'vehicleId', this.form.vehicle.id)
    }
    this.checkVehicleLicence(this.form.vehicleId)

    this.$nextTick(() => {
      this.cleanUnsavedChanges()
    })
  },

  watch: {

    form: {
      handler() {
        this.setUnsavedChanges()
      },
      deep: true
    },

    'form.statementMode': {
      handler(newMode, oldMode) {
        if (newMode === 'API' && !this.isConnectedVehicle) {
          this.meters = []
          MeterDeviceRepository.getdomoconsometers().then(data => {
            this.meters = data.data.map(d => ({text: d.name, value: d._id}))
          }).catch(err => {
            this.notifyError(err)
          })
        }
      },
      deep: true
    },
    'form.vehicleId'(id) {
      this.checkVehicleLicence(id)
    },
    'form.meterIdFromApi'(newMode) {
      if (newMode) {
        const meter = this.meters.find(m => m.value === newMode)
        this.form.meterNameFromApi = meter?.text
      }
    }
  },

  computed: {
    id() {
      return this.$route.params.id ?? null
    },

    meterDevice() {
      return this.$parent.meterDevice
    },

    isExternalOperator() {
      return this.form.type === 'external-operator'
    },

    isConnectedVehicle() {
      return this.form.type === 'connected-vehicle'
    },

    statementModes() {
      // For a connected vehicle the API mode reads "API véhicule" (the global label is "API Domoconso").
      const apiText = this.isConnectedVehicle
          ? this.$t('meter-device.connected-vehicle.statement-mode')
          : this.$t('enums.statement-mode.API')
      return [
        {text: this.$t('enums.statement-mode.IMPORT'), value: 'IMPORT'},
        {text: apiText, value: 'API'},
        {text: this.$t('enums.statement-mode.MANUAL'), value: 'MANUAL'},
      ]
    },

    statementTypes() {
      return [
        {text: this.$t('enums.statement-type.INDEX'), value: 'INDEX'},
        {text: this.$t('enums.statement-type.SESSION'), value: 'SESSION'},
      ]
    },

    billingCycles() {
      const items = []
      let types = this.$t('enums.billing-cycle');
      for(let type in types) {
        items.push({text: types[type], value: type})
      }
      return items
    },

    location: {
      get() {
        if(this.form?.location) {
          return {...this.form?.location, organization: this.form?.organization, organizationId: undefined}
        }
        return null
      },

      set(value) {
        const location = this.cloneObj(value)
        const { organization } = location
        delete location.organization
        this.form.location = location
        this.form.organization = organization
      }
    }
  },

  methods: {
    cancel() {

    },

    onVehicleSelected(vehicle) {
      // Prefill the meter name from the vehicle (overridable) — only when empty.
      if (vehicle && !this.form.name) {
        this.$set(this.form, 'name', vehicle.name || vehicle.registrationNumber || vehicle.vin || null)
      }
    },

    checkVehicleLicence(id) {
      if (!id) {
        this.vehicleLicenceOk = null
        return
      }
      VehicleRepository.connectedVehicleMeterLicence(id).then(res => {
        this.vehicleLicenceOk = res.hasLicence === true
      }).catch(() => {
        this.vehicleLicenceOk = null
      })
    },

    loadTypes() {
      MeterDeviceRepository.metertypes().then(data => {
        this.types = data.map(d => ({text: this.$t('enums.meter-device.' + d), value: d}))
      }).catch(err => {
        this.notifyError(err)
      })
    },

    loadExternalOperatorSuppliers() {
      return new Promise((resolve, reject) => {
        MeterDeviceRepository.externalOperatorSuppliers().then(data => {
          this.externalOperatorSuppliers = data
          resolve(this.externalOperatorSuppliers)
        }).catch(err => {
          this.notifyError(err)
          reject(err)
        })
      })
    },


    async save() {
      if (!this.location) {
        this.notifyError('location.select-location')
        return
      }

      if (this.$refs.form.validate()) {
        this.showLoading(true)

        try {
          const form = this.cloneObj(this.form)

          form.initialMeterValue = form.initialMeterValue * 1000
          form.lastMeterValue = form.lastMeterValue * 1000
          form.maxMeterValue = form.maxMeterValue * 1000

          const locationId = form.location?.id
          const customerId = form.customer?.id
          const organizationId = form.organization?.id

          // Prepare cost centers for main PUT request
          let costCenters = []
          if (form.costCenters && form.costCenters.length > 0) {
            // Normalize cost centers from backend format (id/costCenterId, name/costCenterName)
            costCenters = form.costCenters.map(cc => ({
              costCenterId: cc.costCenterId || cc.id,
              sharePercent: parseFloat(cc.sharePercent)
            }))

            // Validate total = 100%
            const total = costCenters.reduce((sum, cc) => sum + cc.sharePercent, 0)
            if (Math.abs(total - 100) > 0.01) {
              this.$dialog.notify.error(this.$t('cost-center.invalid-allocation'))
              this.showLoading(false)
              return
            }
          }

          delete form.location
          delete form.customer
          delete form.organization
          delete form.costCenters

          const data = {
            ...form,
            locationId,
            customerId,
            organizationId,
            costCenters // Add cost centers to the main PUT
          }

          // Single PUT request with cost centers included
          await MeterDeviceRepository.update(this.id, data)

          this.showLoading(false)
          this.$dialog.notify.success(this.$t('update-success'))
          await this.$parent.loadData()
          this.cleanUnsavedChanges()
          this.$router.push({name: 'meter-device.detail'})
        } catch (err) {
          this.showLoading(false)
          this.notifyError(err)
        }
      } else {
        this.notifyError('form-has-errors')
      }
    },



    // This can also prevent copy + paste invalid character
    filterInput(e){
      // e.target.value = e.target.value.replace(/[^0-9]+/g, '');
    }

  }
}
</script>

<style lang="scss">
.meter-device-edit {

  .meter-device-location {
    width: 80%;
    float: right;
  }

  .vue-tel-input-vuetify {
    //transform: translateY(-8px);
    .v-select {
      min-width: unset;
      background-color: white;
      border-radius: 4px;
      border: 1px solid #758093 !important;
      box-shadow: 0 10px 30px #00000029 !important;
      padding-left: 5px;
      padding-right: 15px;
    }

    & > .v-text-field {
      transform: translateY(-11px);
    }
}


}
</style>
